import { type FC, type ReactNode } from "react";
import { Pencil, Plus } from "lucide-react";

import { cn } from "@/shared/utils/core";
import { formatCurrency, formatDollars } from "@/shared/utils/formatters";
import { groupDebtsByType } from "@/shared/utils/estateMath";

import type { EstateItem } from "@/shared/types/estateTypes";

import { findLinkedSecuredDebts } from "../utils/estateItemUtils";
import { Button } from "@/shared/components/ui/button";
import { isEstimatedItem } from "@/shared/utils/balanceEstimates";

import { AccountRow, ExtractedFromLink } from "@/pages/postloss/modules/components/AccountRow";
import { getCardDisplay } from "./categoryCardDisplay";

const MARKED_AS_NONE = "__MARKED_AS_NONE__";

function isMarkedAsNone(item: EstateItem): boolean {
  return item.notes === MARKED_AS_NONE;
}

interface CategoryCardProps {
  title: string;
  classification: "asset" | "debt";
  subCategories: string[];
  items: EstateItem[];
  showEmpty: boolean;
  onAddItem?: (category: string) => void;
  onEditSection?: () => void;
  editSectionLabel?: string;
  onEditItem?: (item: EstateItem) => void;
  onSelectItem?: (item: EstateItem) => void;
  renderItemActions?: (item: EstateItem) => ReactNode;
  allItems?: EstateItem[];
}

export const CategoryCard: FC<CategoryCardProps> = (props) => {
  const {
    title,
    classification,
    subCategories,
    items,
    showEmpty,
    onAddItem,
    onEditSection,
    editSectionLabel,
    onEditItem,
    onSelectItem,
    renderItemActions,
    allItems,
  } = props;
  const realItems = items.filter((item) => !isMarkedAsNone(item));
  const itemCount = realItems.length;
  const valuableItems = realItems.filter((item) => !item.value_label);
  const totalValue = valuableItems.reduce((sum, item) => sum + item.balance, 0);
  const estimatedCount = valuableItems.filter(isEstimatedItem).length;
  const isDebt = classification === "debt";

  const grouped: Record<string, EstateItem[]> = {};
  for (const item of realItems) {
    const securedType =
      item.item_type === "Home equity loan" ? "Home equity loans" : item.item_type;
    const key =
      item.category === "Secured debts"
        ? securedType || (item.linked_item_id ? "Mortgages" : item.category)
        : item.category;
    if (!grouped[key]) {
      grouped[key] = [];
    }
    grouped[key].push(item);
  }

  const formattedTotal = formatDollars({ amount: totalValue, rounding: "whole" });
  const displayTotal =
    isDebt && totalValue > 0
      ? `-${formattedTotal}`
      : estimatedCount > 0
        ? `~${formattedTotal}`
        : formattedTotal;

  const categoryForSubLabel: Record<string, string> = {};
  const expandedSubCategories = subCategories.flatMap((cat) => {
    if (cat !== "Secured debts") return [cat];
    const itemTypes = Object.keys(grouped).filter((key) =>
      grouped[key].some((i) => i.category === "Secured debts"),
    );
    for (const t of itemTypes) categoryForSubLabel[t] = "Secured debts";
    return itemTypes.length > 0 ? itemTypes : [cat];
  });

  const visibleSubCategories = showEmpty
    ? expandedSubCategories
    : expandedSubCategories.filter((cat) => grouped[cat]);

  return (
    <div className="flex flex-col gap-6 rounded-lg border border-primary bg-white p-4 sm:p-6">
      <div className="flex items-baseline justify-between">
        <div className="flex items-baseline gap-2">
          <p className="text-base font-semibold">{title}</p>
          <p className="text-sm text-secondary">
            {itemCount} {itemCount === 1 ? "item" : "items"}
          </p>
        </div>
        <span
          className={cn(
            "text-base font-semibold",
            isDebt && totalValue > 0 ? "text-error" : "text-primary",
          )}
        >
          {displayTotal}
        </span>
      </div>

      <div>
        {itemCount === 0 && !showEmpty ? (
          onAddItem ? (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => onAddItem(subCategories[0])}
            >
              <Plus className="h-4 w-4" />
              Add
            </Button>
          ) : (
            <p className="text-secondary">No items</p>
          )
        ) : (
          visibleSubCategories.map((subCategory) => (
            <div key={subCategory} className="mb-6 last:mb-0">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-2xs font-semibold uppercase tracking-widest text-secondary">
                  {subCategory}
                </p>
                {onAddItem && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onAddItem(categoryForSubLabel[subCategory] ?? subCategory)}
                  >
                    <Plus className="h-4 w-4" />
                    Add
                  </Button>
                )}
              </div>

              {grouped[subCategory] ? (
                <div className="flex flex-col gap-3">
                  {grouped[subCategory].map((item) => {
                    const actions = renderItemActions?.(item);
                    const hasPendingStatus =
                      item.extracted_from_document && item.extraction_status === "pending";
                    const card = getCardDisplay(item);

                    if (hasPendingStatus && actions) {
                      return (
                        <div
                          key={item.id}
                          className="overflow-hidden rounded-md border border-brand [&>div]:border-0 [&>div]:rounded-none"
                        >
                          <AccountRow
                            item={item}
                            onSelect={onSelectItem}
                            hideExtractedLabel
                            displayTitle={card.title}
                            displaySubtitle={card.subtitle}
                          />
                          <hr className="border-brand" />
                          <div
                            className="flex min-w-0 items-center justify-between gap-2 px-4 py-3"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {item.source_document ? (
                              <ExtractedFromLink
                                sourceDocument={item.source_document}
                                className="mt-0 min-w-0 self-center"
                              />
                            ) : null}
                            <div className="flex shrink-0 items-center gap-2">{actions}</div>
                          </div>
                        </div>
                      );
                    }

                    const linkedDebts = allItems ? findLinkedSecuredDebts(item, allItems) : [];

                    if (linkedDebts.length > 0) {
                      const totalDebt = linkedDebts.reduce((sum, d) => sum + d.balance, 0);
                      const netValue = item.balance - totalDebt;
                      const groupedDebts = Object.fromEntries(
                        Object.entries(groupDebtsByType(linkedDebts)).map(([type, items]) => [
                          type,
                          items.reduce((sum, d) => sum + d.balance, 0),
                        ]),
                      );
                      return (
                        <div
                          key={item.id}
                          className="overflow-hidden rounded-md border border-primary [&>div:first-child]:border-0 [&>div:first-child]:rounded-none"
                        >
                          <AccountRow
                            item={item}
                            onEdit={onEditItem}
                            onSelect={onSelectItem}
                            displayTitle={card.title}
                            displaySubtitle={card.subtitle}
                          />
                          <div className="mx-4 divide-y divide-primary">
                            {Object.entries(groupedDebts).map(([type, balance]) => (
                              <div key={type} className="flex items-center justify-between py-3">
                                <p className="text-sm">
                                  <span className="font-medium">{type}</span>
                                  <span className="text-secondary">
                                    {" "}
                                    &middot; Remaining balance
                                  </span>
                                </p>
                                <p className="text-sm font-medium">-{formatCurrency(balance)}</p>
                              </div>
                            ))}
                            <div className="flex items-center justify-between py-3">
                              <p className="text-sm font-medium">Net to estate</p>
                              <p className="text-sm font-medium">{formatCurrency(netValue)}</p>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <AccountRow
                        key={item.id}
                        item={item}
                        onEdit={onEditItem}
                        onSelect={onSelectItem}
                        actions={actions}
                        displayTitle={card.title}
                        displaySubtitle={card.subtitle}
                      />
                    );
                  })}
                </div>
              ) : (
                <p className="text-tertiary">None</p>
              )}
            </div>
          ))
        )}

        {onEditSection && (
          <Button
            onClick={onEditSection}
            size="lg"
            variant="outline"
            className="mt-2 w-full items-center justify-center gap-2"
          >
            {editSectionLabel ? <Plus className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
            {editSectionLabel ?? "Edit section"}
          </Button>
        )}
      </div>
    </div>
  );
};
